import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { NO_CALC_YET, RECORD_NOT_FOUND, useCase, useLatestCalculation } from "@/api";
import { Button } from "@/components/Button";
import { CaseGone } from "@/components/CaseGone";
import { StateMessage } from "@/components/StateMessage";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

import { CalculationScreen } from "./CalculationScreen";
import { CalculationSkeleton } from "./CalculationSkeleton";
import { EmptyCalculation } from "./EmptyCalculation";
import { RUN_STATUS_KEY } from "./useRunCalculation";

/**
 * `/vorgang/:caseId/kalkulation` (design §9, D-22): reads the Vorgang and
 * its latest calculation, writes nothing. A 404 on the calculation is the
 * ordinary answer for a Vorgang nobody has priced yet and opens the empty
 * state; the Vorgang itself supplies `latestVersionNo`, the number every run
 * from here posts against, and `gratingType`, which the first run's mesh
 * depends on.
 *
 * Carries the one persistent live region for the empty state → screen swap
 * (S-3, fix round 2, items 8 and 16): this route's own child swaps from
 * `EmptyCalculation` to `CalculationScreen` the instant a run lands,
 * unmounting the empty state's own live region before it could ever
 * announce anything — but this route itself never unmounts across that
 * swap, so its own `role="status"` paragraph is a region a screen reader is
 * already listening to when the text inside it changes, unlike one that
 * appears for the first time already full. `EmptyCalculation` still seeds
 * `RUN_STATUS_KEY` on the very run that causes the swap; this route reads
 * that same key back — through `useQuery` in `enabled: false` mode, which
 * subscribes to the cache without ever fetching, so it reacts to that write
 * even though this component was mounted long before it happened (unlike
 * the child, which is mounted *by* that write). Cleared once this route
 * itself is left, not on every render, so the sentence stays on screen for
 * as long as this Vorgang is open and does not replay on a later visit.
 */
export function CalculationRoute() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const client = useQueryClient();
  const detail = useCase(caseId ?? null);
  // A Vorgang that is gone is named by `CaseGone`, whose effect runs before
  // this one — naming the calculation here would overwrite it.
  useDocumentTitle(detail.error?.message === RECORD_NOT_FOUND ? null : "Kalkulation", detail.data?.caseNo);
  const latest = useLatestCalculation(caseId ?? null);
  const runStatus =
    useQuery({
      queryKey: RUN_STATUS_KEY(caseId ?? ""),
      queryFn: (): Promise<string> => Promise.resolve(""),
      enabled: false,
      retry: false,
    }).data ?? "";

  useEffect(() => {
    if (!caseId) return undefined;
    return () => {
      client.removeQueries({ queryKey: RUN_STATUS_KEY(caseId), exact: true });
    };
  }, [caseId, client]);

  if (!caseId) return <Navigate to="/" replace />;

  const noCalcYet = latest.error?.message === NO_CALC_YET;
  // Handed over (or never mine): the record's own state, the new owner named
  // where known, and „Zum Register" (round-5 screen check, R5-6) — read
  // before the data, which the cache keeps beside the 404. The Vorgang's own
  // answer decides: the calculation's 404 means „none yet" (`NO_CALC_YET`).
  const gone = detail.error?.message === RECORD_NOT_FOUND;

  let content: ReactNode;
  if (gone) {
    content = <CaseGone caseId={caseId} onRegister={() => navigate("/vorgaenge")} />;
  } else if (detail.isPending || latest.isPending) {
    // In the calculation's own geometry, under the frame that already names
    // the Vorgang — never a loading line (Task 10, B-4).
    content = <CalculationSkeleton />;
  } else if (detail.error || !detail.data) {
    content = (
      <StateMessage title="Vorgang lässt sich nicht öffnen">
        {detail.error?.message ?? "Vorgang konnte nicht geladen werden."}
        <div>
          <Button variant="quiet" onClick={() => navigate("/")}>
            Zum Start
          </Button>
        </div>
      </StateMessage>
    );
  } else if (latest.error && !noCalcYet) {
    content = (
      <StateMessage title="Kalkulation lässt sich nicht öffnen">
        {latest.error.message}
        <div>
          <Button variant="quiet" onClick={() => navigate(`/vorgang/${caseId}/planung`)}>
            Zurück zur Planung
          </Button>
        </div>
      </StateMessage>
    );
  } else if (noCalcYet || !latest.data) {
    content = <EmptyCalculation caseId={caseId} detail={detail.data} />;
  } else {
    content = <CalculationScreen caseId={caseId} detail={detail.data} calc={latest.data} />;
  }

  return (
    <>
      {/* Heard, not seen (Generalprobe 15.10.2026): visible, it stood above
          the title as „Kalkulation 1 gerechnet." for the rest of the visit,
          beside the screen's own line saying „Kalkulation 3 gerechnet.". The
          swap from the empty state to the table is what a sighted person
          sees; this region is for the one who does not. */}
      <p role="status" className="visually-hidden">
        {runStatus}
      </p>
      {content}
    </>
  );
}
